/**
 * ENG-5641 — read-only CLI connectivity probe (manager CLI).
 *
 * Runs a single read-only command (e.g. `<bin> --version`, `gcloud version`)
 * and maps the result to a ConnectivityProbeOutcome. The manager already
 * installs/manages these CLI tools, so this just confirms the binary is on
 * PATH and answers — the cheapest "is this tool usable" signal.
 *
 *   - exit 0            → 'ok'
 *   - ENOENT (missing)  → 'unverified' + cause 'not_run' (see ENG-8902)
 *   - timeout           → 'transient_error'
 *   - non-zero exit     → 'down', UNLESS the diagnostic (stderr, stdout, or
 *                         execFile's own message) names a cause that is not
 *                         the credential's fault (see TRANSIENT_SIGNATURES), or
 *                         says the probe never ran at all (DID_NOT_RUN_SIGNATURES
 *                         → 'unverified' + cause 'not_run')
 *
 * The `cause` on the two did-not-run branches is the part that SURVIVES. The
 * messages below are written for a human and then dropped before persistence —
 * `integration-connectivity-report.ts` records `last_connectivity_cause` and not
 * the prose — so without it a row that was never probed and a row that was
 * probed and failed are indistinguishable on the record. See ConnectivityCause
 * in `packages/core/src/integrations/integration-health.ts` for the measurement
 * that made that concrete.
 *
 * Read-only by contract: callers pass only inspection args (the resolver's
 * cliArgs, defaulting to `--version`). Never runs a mutating subcommand.
 *
 * ENG-8902 — WHY ENOENT IS NOT `down`.
 *
 * A missing binary means this probe DID NOT RUN. That is not an observation
 * about the integration, and reporting it as `down` asserts a fact the probe
 * never established.
 *
 * Not hypothetical. `gh` is provisioned PER AGENT under `.claude/agt-bin/`
 * (the ENG-8344 credential shim), while this probe runs in the manager's
 * process, whose PATH does not include it. Every GitHub integration on a host
 * therefore read `last_connectivity_status: down` with a climbing failure
 * count while GitHub worked perfectly — on 2026-08-15 a branch was pushed and
 * a PR opened with that exact binary four minutes before the probe called it
 * down. Two agents on completely different auth models (`oauth2` and
 * `github_app`) showed the identical verdict, which is the tell: the result
 * did not depend on the integration at all.
 *
 * `unverified` is the honest verdict and the vocabulary already carries it —
 * "not measured, rather than measured-and-fine". It sits BELOW `down` in
 * CONNECTIVITY_SEVERITY, so a real observation of trouble from any other leg
 * still outranks it; this cannot mask a genuine outage.
 *
 * A false `down` is not the lesser evil against a false `ok`. It teaches every
 * reader — human and agent — that the field means nothing, and then the true
 * negatives get ignored too. It also actively misleads: during the ENG-8900
 * investigation this `down` looked like corroboration of a real credential
 * outage, when the probe would have said `down` either way.
 *
 * ENG-5463 follow-up — WHY the non-zero branch is no longer a flat `down`.
 *
 * The paragraph above used to end "this just confirms the binary is on PATH
 * and answers — the cheapest 'is this tool usable' signal", and that was an
 * honest description while every caller passed `--version`. A liveness check
 * has exactly two outcomes and "it errored" really does mean "needs
 * attention".
 *
 * ENG-5463 changed what runs here without changing that failure model. It
 * routed CLI-backed toolkits seeded `native` onto this lane, and github's
 * curated args are `gh auth status` — a CREDENTIAL check. Suddenly a non-zero
 * exit could mean the token is dead, or that GitHub rate-limited us, or
 * returned a 502, or that DNS blinked, or that OUR OWN broker could not mint
 * the token. Only the first is `down`.
 *
 * That matters because `down` is not a label, it is a ladder. What a `down`
 * from THIS lane actually does, traced rather than assumed (CodeRabbit pushed
 * back on an earlier draft of this paragraph and it was right to):
 *
 *   - writes `last_connectivity_status: 'down'` and increments
 *     `consecutive_connectivity_failures` on the integration row;
 *   - shows the integration in an error state in the console, so a human
 *     reading the row is told a working credential is broken;
 *   - once the streak reaches the hysteresis threshold, opens an
 *     `integration_down` alert paging the team channel and the agent owner
 *     (`integration-connectivity-escalation`).
 *
 * What it does NOT do, and the earlier draft wrongly claimed it did: flip the
 * install to `needs_reauth`. That flip lives in the managed-connection-health
 * cron, which only walks `auth_type = 'managed'` Composio rows; the CLI lane
 * carries native/CLI-backed toolkits, and the alert evaluator never touches
 * the integration `status` enum. The sibling HTTP lane DOES reach that flip,
 * which is why its comment says so and this one must not.
 *
 * A false page and a false error badge are still the wrong outcome, and this
 * is not hypothetical. During the 2026-08-14 identity-tier outage (ENG-8869)
 * `gh auth status` failed on every agent on the fleet, because the ENG-8344
 * per-use credential fetch could not reach the broker. Under the old flat rule
 * that is a fleet-wide "your GitHub is broken" — from a provider outage that
 * had nothing to do with GitHub.
 *
 * DIRECTION OF THE DEFAULT, deliberately conservative and not my own idea —
 * it is the rule Brad set on the HTTP lane in the sibling fix, and the two
 * lanes should not disagree about this:
 *
 *     "A false `down` is loud and wrong; a false `transient_error` is quiet
 *      and wrong, and quiet is worse."
 *
 * So `down` stays the default and a signature has to opt IN to `transient`.
 * A missed signature costs a false page. A greedy signature costs a dead
 * credential that never escalates, and nobody finds that one.
 */

import { execFile } from 'node:child_process';
import type { ConnectivityCause, ConnectivityProbeOutcome } from '@augmented/core/integrations';

const DEFAULT_TIMEOUT_MS = 8_000;

/**
 * The context a bare status number has to appear in before we read it as one.
 *
 * `\b429\b` on its own is not a rate limit — it is a version fragment
 * (`gh 2.429.0`), a repo count, a port, a line number. The previous draft
 * claimed to be "anchored on an explicit HTTP-status context" while actually
 * matching the bare number; this is that claim made true.
 */
const STATUS_CTX = String.raw`(?:\bhttps?\/?\s*(?:1\.[01]\s+)?|\bstatus(?:\s*code)?\s*[:=]?\s*|\bcode\s*[:=]?\s*|\breturned\s+(?:http\s*)?)`;

/**
 * ENG-5463 follow-up — failure signatures that mean "not the credential's
 * fault", each with the operator-facing reason it maps to. Matched against
 * stderr, stdout and execFile's own message.
 *
 * Every pattern is anchored on wording a CLI actually emits, or on a number
 * behind STATUS_CTX — never on a bare number, which would hit a version
 * string, a repo count or a port. The cost of a greedy pattern here is a dead
 * credential graded `transient_error` forever, which is the silent failure
 * this whole ticket exists to remove — so each entry is narrow on purpose.
 */
const TRANSIENT_SIGNATURES: ReadonlyArray<{
  re: RegExp;
  reason: string;
  /**
   * ENG-10529 — the `ConnectivityCause` this signature has ALREADY established.
   *
   * This is not a second, weaker classification bolted onto the message. It is
   * the same judgement the `reason` beside it already encodes, written in the
   * vocabulary the escalation layer reads instead of the one an operator reads.
   * If a signature is trusted enough to decide the STATUS, it is trusted enough
   * to name the CAUSE: identical evidence, identical confidence, no new
   * inference. That matters because `ConnectivityProbeOutcome.cause` forbids
   * guessing outright — *"Never inferred from the message: guessing a cause at
   * an escalation boundary is how ENG-8355 / ENG-6428 happened"* — and nothing
   * here infers anything the line above it did not already conclude.
   *
   * OPTIONAL, and the one omission is deliberate rather than unfinished. See
   * the broker entry below.
   */
  cause?: ConnectivityCause;
}> = [
  // Vendor rate limiting. RFC 6585 for 429; GitHub's secondary limits and
  // Google's quota wording do not use the number at all.
  { re: new RegExp(`${STATUS_CTX}429\\b|too many requests|rate limit|secondary rate|quota exceeded|abuse detection`, 'i'),
    reason: 'rate limited by the provider',
    // BEHAVIOUR CHANGE, and it is the point rather than a side effect.
    // `rate_limited` is one of the two `isVendorSideCause` members, so
    // `isTransientStreakPermanent` stops promoting a long 429 streak to the
    // hard-down path. That is exactly what ENG-9173 asked for: "59 consecutive
    // Buffer 429s paged at CRITICAL, next to genuinely actionable criticals,
    // naming no action at all, because nobody can un-429 a vendor." Until now
    // the cause arrived as `unknown`, which is NOT vendor-side, so the
    // suppression that ticket built could never fire on this lane.
    cause: 'rate_limited' },
  // Vendor-side server errors. Anchored on the phrase or on an explicit
  // HTTP-status context so a bare 3-digit number cannot match.
  { re: new RegExp(`${STATUS_CTX}(?:502|503|504)\\b|bad gateway|service unavailable|gateway time-?out|internal server error|server error \\(5\\d\\d\\)`, 'i'),
    reason: 'provider returned a server error',
    // Also vendor-side, same consequence as above and for the same reason:
    // "the vendor returned 5xx — their fault, ours to watch."
    cause: 'server_error' },
  // Transport. The provider may be fine and simply unreachable from here.
  { re: /ECONNREFUSED|ECONNRESET|ETIMEDOUT|ENOTFOUND|EAI_AGAIN|EHOSTUNREACH|ENETUNREACH|connection refused|connection reset|network is unreachable|temporary failure in name resolution|tls handshake|dial tcp/i,
    reason: 'network failure reaching the provider',
    // NOT vendor-side, so escalation is unchanged here — `unreachable` "may be
    // US, which is why it counts as actionable". Naming it changes what an
    // operator READS, not whether they are told. Same call `networkOutcome`
    // made for the HTTP lane.
    cause: 'unreachable' },
  // OUR failure, not the credential's. ENG-8344 fetches the token per use
  // through the platform, so an identity-tier outage surfaces here as a
  // non-zero exit from a CLI whose credential never became available. Grading
  // that `down` blames the customer's token for our own outage — it is the
  // exact case that would have paged the fleet on 2026-08-14.
  { re: /credential fetch failed|key exchange failed|host\/exchange|upstream connect error|could not read Username/i,
    reason: 'our credential broker could not supply a token',
    // DELIBERATELY UNCAUSED — the one entry in this table with no `cause`, and
    // it is a decision, not an oversight. Nothing in the closed vocabulary
    // describes "our own broker failed":
    //   - `auth_rejected` is actively wrong. It means the CUSTOMER'S credential
    //     was refused, and its remedy is "reconnect" — telling a customer to
    //     re-auth a working token during OUR outage is precisely the 2026-08-14
    //     fleet-wide misfire this signature was written to prevent.
    //   - `server_error` means THE VENDOR returned 5xx, and it is vendor-side,
    //     so it would SUPPRESS escalation of an outage that is ours and that we
    //     most want to be woken for.
    //   - `unreachable` is a network fact about the provider; the broker is
    //     reachable and answering, just not with a token.
    // So the honest value is `unknown`, which stays actionable, and a wrong
    // cause is worse than no cause because it sends an operator somewhere
    // specific. Fixing this properly needs a new enum member (a `broker_*`
    // cause), which is a schema decision and is out of scope here.
  },
];

/**
 * First matching signature, or null when nothing recognises the failure.
 *
 * ENG-10529: returns the ENTRY rather than its `reason` string. The cause and
 * the prose are two renderings of one judgement, and handing back only the
 * prose is what let the cause be dropped on the floor for the whole lane.
 */
function transientSignatureFor(
  text: string,
): { reason: string; cause?: ConnectivityCause } | null {
  for (const { re, reason, cause } of TRANSIENT_SIGNATURES) {
    if (re.test(text)) return cause ? { reason, cause } : { reason };
  }
  return null;
}

/**
 * ENG-10529 — why a probe that RAN and was REFUSED was refused.
 *
 * Reached only by the `down` fallback, which means every gate above it has
 * already declined: the binary launched (or the ENOENT branch would own it),
 * it was not a did-not-run shape, and no transient signature matched. So the
 * question here is narrow and answerable: the CLI ran, the remote answered,
 * and the answer was a refusal — whose?
 *
 * ONLY `auth_rejected`, and the omission of `access_restricted` is the
 * deliberate part. The two wear the same status codes and it is tempting to
 * split them here; that would be wrong twice over. `access_restricted` means
 * OUR PLATFORM credential was refused (the ENG-9770 Composio IP-allowlist
 * case), not the customer's, and its own docs say it "pairs with `unverified`,
 * never `down`" — so a signature producing it on THIS branch would attach a
 * cause whose whole meaning contradicts the status beside it. On this lane the
 * token is the integration's own grant, so a refusal is `auth_rejected`: "the
 * INTEGRATION'S credential was refused (401, or a 403 that is not throttling).
 * The one a human fixes by reconnecting."
 *
 * NARROW ON PURPOSE, exactly as TRANSIENT_SIGNATURES is, and the asymmetry of
 * the risk is the reason. An unmatched failure stays `unknown`, which is
 * treated as ACTIONABLE and escalates — loud and vague, and recoverable. A
 * WRONG `auth_rejected` is quiet and specific: it points a human at a
 * credential that is fine and sends them to re-run an OAuth round-trip that
 * cannot help. So every pattern below is either measured output or wording no
 * other program emits, and anything unrecognised is left alone.
 */
const DOWN_SIGNATURES: ReadonlyArray<{ re: RegExp; cause: ConnectivityCause }> = [
  {
    // MEASURED, not imagined. Captured 2026-09-16 from `gh api user` on this
    // host against a GitHub App token lacking the permission:
    //   {"message":"Resource not accessible by integration", … "status":"403"}
    //   gh: Resource not accessible by integration (HTTP 403)
    // A 403 that is not throttling — the rate-limit signature is checked first
    // and owns the throttling 403s, so reaching here means it is not one.
    //
    // The rest are GitHub's and git's own refusal wording. `Bad credentials` is
    // the literal body of GitHub's 401; the "not logged into any … hosts"
    // sentence is `gh auth status`'s, and is already pinned as a `down` case in
    // eng-5463-cli-probe-transient.test.ts.
    re: new RegExp(
      `resource not accessible by integration|bad credentials|` +
        `not logged into any\\b[^\\n]*\\bhosts|` +
        `requires authentication|authentication failed|invalid authentication|` +
        `${STATUS_CTX}401\\b|unauthorized\\b`,
      'i',
    ),
    cause: 'auth_rejected',
  },
];

/**
 * The cause for a refusal, or undefined when nothing recognises it.
 *
 * Undefined rather than `'unknown'`: the runner omits an absent cause
 * deliberately (`...(outcome.cause ? { cause } : {})`) so that a manager which
 * COULD NOT classify stays distinguishable on the wire from one too old to
 * try, and the API coerces the absent value to `'unknown'` anyway. Sending an
 * explicit `'unknown'` would erase that distinction and change nothing
 * persisted — see ENG-10529's own comment thread, where that was criterion 2
 * until it turned out to need an enum member rather than a line of code.
 */
function downCauseFor(text: string): ConnectivityCause | undefined {
  for (const { re, cause } of DOWN_SIGNATURES) {
    if (re.test(text)) return cause;
  }
  return undefined;
}

/**
 * ENG-8902 follow-up — failures that mean THE PROBE NEVER RAN, one level down.
 *
 * The ENOENT branch below already handles "the binary is not on PATH", because
 * a probe that could not start has observed nothing and must not assert a
 * verdict. But `gh` on an agent is not the real binary: it is the ENG-8344
 * credential shim at `.claude/agt-bin/gh`, a wrapper that locates the real
 * `gh`, injects a brokered token and execs it.
 *
 * So when the real binary is missing, the shim IS found and DOES start —
 * ENOENT never fires — and the shim exits 127 having run nothing. The failure
 * arrives as an ordinary non-zero exit, falls past every transient signature
 * (it is not a rate limit, a 5xx or a socket error) and lands on the flat
 * `down`. The result is the precise outage ENG-8902 was raised to stop,
 * re-entering through the wrapper: on 2026-08-16 eight agents carried CRITICAL
 * "GitHub down" alerts with 54-224 consecutive failures while `gh` worked well
 * enough for me to merge two pull requests through it in the same minute one
 * of those probes returned `down`.
 *
 * The distinction the ENOENT branch draws — "could not run" is not "is down" —
 * does not depend on WHICH layer failed to find the tool, so neither should
 * the verdict. Matched on the wrapper's own wording rather than on the bare
 * exit code: 127 from the *provider's* CLI could legitimately mean something
 * else, and a verdict-softening rule must never be the greedy one.
 *
 * Deliberately NOT fixed here: the reason those hosts' probes cannot see the
 * real `gh`. I could not reproduce that from the environment I have, so the
 * root cause is still open — this change stops the false CRITICALs either way,
 * and `unverified` keeps the situation visible instead of asserting health.
 */
const DID_NOT_RUN_SIGNATURES: ReadonlyArray<{
  re: RegExp;
  reason: string;
  /**
   * Exit statuses this signature is additionally gated on, when the wording
   * alone is not conclusive. Omitted ⇒ the wording is conclusive by itself
   * (the shim's own sentence and the AGT_AGENT_ID case both are — no other
   * program emits them).
   */
  exitCodes?: readonly number[];
}> = [
  {
    re: /the real \S+ binary is not on PATH|only the Augmented shim is/i,
    reason:
      'the Augmented credential shim ran but could not find the real binary, so nothing was probed',
  },
  {
    // ENG-8902: the PROBER's own child process is missing AGT_AGENT_ID, so the
    // credential shim cannot identify which agent to mint for. It never reaches
    // GitHub, and it never can — this is a defect in how the probe is spawned
    // (ENG-9005), not a statement about the integration.
    //
    // Measured live on 2026-08-18T07:16:30Z, gordon / my-second-host:
    //   "gh probe inconclusive — our credential broker could not supply a
    //    token: augmented: GitHub credential fetch failed: AGT_AGENT_ID is not
    //    set in this process environment"
    //
    // It was landing in the TRANSIENT branch, so every occurrence incremented
    // consecutive_connectivity_failures. That is how gordon reached 85 and ada
    // 234 against credentials that mint on demand — the streak counts prober
    // failures and calls them connectivity failures. The probe's own message
    // already said "inconclusive"; the grading disagreed with the wording.
    //
    // DELIBERATELY NARROW. A broker failure is NOT automatically did-not-run:
    // an HTTP 500 from the credential endpoint is a real transient that should
    // keep its transient grading (I hit one tonight mid-session; it recovered
    // on retry). Only the missing-env case is unambiguously unrunnable, so only
    // that signature is matched here.
    // ENG-9122: matched on `AGT_AGENT_ID is not set` rather than the full
    // "…in this process environment" sentence, because there is a SECOND
    // emitter that does not use that wording: `packages/mcp/src/index.ts:4035`
    // throws `Cannot forward tool call: AGT_AGENT_ID is not set`. Under the
    // longer pattern that one graded `transient_error` and climbed the failure
    // counter — the exact defect this entry exists to stop, reached by a
    // different door.
    //
    // Still narrow on purpose, and the narrowness is the point: a broker HTTP
    // 500 ("host key exchange failed … returned HTTP 500") does not contain
    // this phrase and therefore stays `transient_error`. That is correct — it
    // is a real intermittent our broker genuinely has, and grading it neutral
    // would hide a fault worth seeing.
    re: /AGT_AGENT_ID is not set/i,
    reason:
      'the probe process was spawned without AGT_AGENT_ID, so the credential shim could not ' +
      'identify the agent and nothing was probed (ENG-9005)',
  },
  {
    // ENG-9810: THE SIBLING VARIABLE. The entry above catches a probe spawned
    // without AGT_AGENT_ID. The shim needs two things to fetch a token, and the
    // other one had no signature at all.
    //
    // REPRODUCED ON agt-aws-1 TODAY, by running this agent's own shim with a
    // stripped environment — not inferred from a log:
    //
    //   augmented: GitHub credential fetch failed: AGT_HOST is not set in this
    //   process environment
    //   augmented: the token is fetched per use from <AGT_HOST unset>/host/...
    //   for agent_id=... (ENG-8344). This is NOT a GitHub permissions problem.
    //
    // The shim's own second line says it: *this is not a GitHub permissions
    // problem*. Without this entry that message matched nothing, fell past every
    // transient signature, and landed on the flat `down` — incrementing
    // `consecutive_connectivity_failures` and, past the threshold, opening an
    // `integration_down` alert that reads as a GitHub outage. The identical
    // defect the AGT_AGENT_ID entry was written to stop, reached through the
    // other missing variable.
    //
    // NARROW, and narrow in exactly the way the entry above is: matched on OUR
    // OWN VARIABLE NAME, which no provider CLI emits. Deliberately NOT matched
    // on the broader `credential fetch failed` shape, even though that would
    // catch both variables in one pattern — a broker HTTP 500 contains that
    // phrase and must KEEP its transient grading, as the entry above states at
    // length. Widening to the shape would silently reverse a decision somebody
    // made on purpose.
    re: /AGT_HOST is not set/i,
    reason:
      'the probe process was spawned without AGT_HOST, so the credential shim had nowhere to ' +
      'fetch a token from and nothing was probed (ENG-8344)',
  },
  {
    // THE INTERPRETER NEVER STARTED — the wrapper could not even be launched.
    //
    // The ENG-8344 credential shim is a Node script beginning
    // `#!/usr/bin/env node`. If the PATH handed to the probe omits the
    // directory holding `node`, the kernel finds and execs the shim, `env`
    // then fails to resolve its interpreter, and the process dies with exit
    // 127 and:
    //
    //     env: 'node': No such file or directory
    //
    // That is a did-not-run in the purest sense — not one byte of `gh` ran —
    // and until now it matched NOTHING. Not the two entries above (the shim
    // never got far enough to print its own wording, and never read
    // AGT_AGENT_ID), and not any TRANSIENT_SIGNATURE (it is not a throttle, a
    // 5xx or a socket error). So it fell through to the flat `down`,
    // incrementing consecutive_connectivity_failures and, past the threshold,
    // opening an `integration_down` alert that reads as a GitHub outage.
    //
    // REPRODUCED on agt-aws-1 2026-08-31, not inferred:
    //   PATH=<agent>/.claude/agt-bin              -> env: 'node': No such file
    //   PATH=<agent>/.claude/agt-bin:/usr/bin     -> shim runs, finds gh 2.96.0
    // and through runCliProbe the first case graded:
    //   status 'down', cause undefined,
    //   "sh exited non-zero: env: 'node': No such file or directory"
    //
    // Worth stating because it corrects a plausible-looking account: a
    // truncated PATH does NOT reach the shim's own "the real gh binary is not
    // on PATH (only the Augmented shim is)" message. It cannot — that line is
    // printed by JavaScript that never executes. Anyone matching the two
    // symptoms by eye will pair the wrong cause with the wrong effect.
    //
    // NARROW, per the file's standing rule that a verdict-softening pattern
    // must never be the greedy one. A bare "No such file or directory" is far
    // too broad — a CLI may print it about a config file, a repo path or a key
    // file while genuinely failing. Both alternatives below are anchored on the
    // LAUNCH failing: `env`'s own diagnostic, and the shell's `bad interpreter`.
    // TIGHTENED IN REVIEW, and the tightening is the interesting part.
    //
    // My first pattern was `env: .{0,64}: No such file or directory`, which is
    // exactly the greedy shape this file's header warns against. `env: ` is not
    // a rare prefix — a CLI is free to print `env: GH_CONFIG_DIR: No such file
    // or directory` about a variable it was told to read, and that is a REAL
    // failure. Softening it to `unverified` would have hidden a dead credential
    // permanently, which the header names as the failure nobody ever finds.
    //
    // Two independent narrowings, either of which alone would be weak:
    //
    //  - QUOTING. GNU coreutils `env` quotes the command it could not exec:
    //    `env: 'node': No such file or directory`. A variable name in a
    //    provider's own diagnostic is not quoted that way. `[^']+` cannot span
    //    the closing quote, so it cannot drift across a longer message.
    //  - EXIT STATUS. A launcher that could not exec its target exits 127
    //    (command not found) or 126 (found, not executable). An ordinary CLI
    //    failure that merely mentions a missing file exits with its own code —
    //    1 in every case I could produce.
    //
    // Requiring both means a match is a launch failure by two agreeing signals,
    // not by a substring. (CodeRabbit.)
    re: /\benv: '[^']+': No such file or directory|: bad interpreter: No such file or directory/i,
    exitCodes: [126, 127],
    reason:
      "the probe's interpreter could not be launched (a truncated PATH omitting the directory " +
      'that holds `node` does this to the credential shim), so nothing was probed',
  },
];

/** First matching did-not-run signature, or null. */
function didNotRunReasonFor(text: string, exitCode: number | null): string | null {
  for (const { re, reason, exitCodes } of DID_NOT_RUN_SIGNATURES) {
    if (!re.test(text)) continue;
    // A signature that names exit statuses must see one of them. Fails CLOSED:
    // an unknown or non-numeric status does not satisfy the gate, so the outcome
    // stays `down`. That is the conservative direction this file insists on — a
    // missed did-not-run costs a false page, a greedy one costs a dead
    // credential that never escalates.
    if (exitCodes && (exitCode === null || !exitCodes.includes(exitCode))) continue;
    return reason;
  }
  return null;
}

/**
 * Credential-shaped substrings, redacted before any of this reaches a message.
 *
 * This outcome's `message` is persisted as `status_message` and shown to
 * operators, so it is an egress point — on BOTH outcomes. `gh auth status`
 * prints `Token: ghs_...` when it SUCCEEDS, so the ok path is the likelier
 * leak of the two, and a CLI is free to start writing one on failure without
 * telling us. Redacting unconditionally costs nothing and means a future
 * vendor decision cannot turn a diagnostic into a leak.
 */
const CREDENTIAL_SHAPED =
  /\b(gh[pousr]_[A-Za-z0-9]{16,}|github_pat_[A-Za-z0-9_]{20,}|xox[abprs]-[A-Za-z0-9-]{10,}|sk-[A-Za-z0-9]{20,}|eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}|AKIA[0-9A-Z]{16})\b/g;

const MAX_MESSAGE_CHARS = 200;

/**
 * Redact credential-shaped substrings and bound the length.
 *
 * Shared by BOTH outcome paths on purpose. It used to live inside the failure
 * formatter, which left the success path quoting raw stdout straight into
 * `status_message` — and the success path is the one `gh auth status` prints
 * `Token: ghs_...` on today, so the unguarded branch was the likelier leak of
 * the two.
 */
function redactAndBound(line: string): string {
  const redacted = line.replace(CREDENTIAL_SHAPED, '<redacted>');
  return redacted.length > MAX_MESSAGE_CHARS
    ? `${redacted.slice(0, MAX_MESSAGE_CHARS - 1)}…`
    : redacted;
}

/**
 * The most useful single line from a failed probe, redacted and bounded.
 *
 * stderr first — it is where these CLIs put the actual reason — then stdout,
 * then execFile's own message.
 *
 * stdout is in the chain because the CLASSIFIER already reads it (`gh` prints
 * some failures there) and it made no sense for the two to disagree: a
 * stdout-only failure could be graded correctly and still persist
 * "Command failed: …" as its `status_message`, so the operator got the right
 * verdict with the reason stripped out of it. Caught by CodeRabbit against the
 * stdout test added in this same round, which is a fair result for a test
 * whose whole point was that this branch was under-exercised.
 */
function firstMeaningfulLine(
  stderr: string | Buffer | undefined,
  stdout: string | Buffer | undefined,
  fallback: string,
): string {
  // The FAILURE lane, so a line gh marked as failed is the reason even when a
  // healthy account is listed above it. The ok lane passes 'ok' and gets the
  // mirror of this rule, not an exemption from it - see firstReasonLine's
  // parameter doc for why the asymmetry was itself a defect.
  const line =
    firstReasonLine(stderr, 'failure') ??
    firstReasonLine(stdout, 'failure') ??
    firstReasonLine(fallback, 'failure') ??
    '';
  return redactAndBound(line);
}

/**
 * ENG-10212 — THE FIRST LINE IS NOT ALWAYS THE REASON, and for the one CLI
 * that has been failing fleet-wide it never is.
 *
 * `gh auth status` prints a SECTION HEADING and then indented detail:
 *
 *     github.com
 *       X Failed to log in to github.com account <name> (GH_TOKEN)
 *       - Active account: true
 *
 * Taking the first non-empty line therefore yields the bare string
 * `github.com`, and the whole message becomes
 * `gh exited non-zero: github.com`. That is not merely uninformative, it is
 * MISLEADING: it reads as a hostname the probe could not reach, so a human
 * triaging it goes and looks at networking. It is a credential fault, and the
 * sentence that says so is on the very next line, discarded.
 *
 * Measured: three GitHub integrations across two hosts and two orgs
 * (`8777de52` stirling 423 failures, `d4d2218d` grace 747, `a2acef45` ledger
 * 745) have carried this byte-identical message for 18 to 33 days, and nobody
 * could act on any of them. The success path on this host prints the same
 * heading shape, which is how the format was confirmed.
 *
 * STRUCTURAL, NOT LEXICAL, deliberately. TWO cases skip the leading line, and
 * the header used to promise only the first while the code did both (obi-wan on
 * #6204 — the same prose-versus-behaviour disagreement this function exists to
 * fix, one level up):
 *   1. the leading line is heading-SHAPED (one bare token, no whitespace,
 *      hostname characters only) AND something MORE indented follows it;
 *   2. the line that would be reported carries the marker that CONTRADICTS the
 *      verdict this line is about to be attached to, and a later line carries
 *      the marker that agrees with it. BOTH LANES, symmetrically — on `failure`
 *      that means a `✓` candidate yields to a later `X ` (the gh multi-account
 *      case, where reporting the healthy line makes a `down` verdict contradict
 *      its own evidence), and on `ok` it means an `X ` candidate yields to a
 *      later `✓` (the mirror, which the heading branch made newly reachable).
 * Otherwise the first meaningful line is returned unchanged.
 *
 * THIS PARAGRAPH SAID "on the FAILURE lane only" UNTIL obi-wan `0b0d254b2b89`,
 * which is worth leaving on the record rather than quietly correcting: the
 * commit that made the rule symmetric was titled "the asymmetry was the defect"
 * and it left the header here still asserting the asymmetry. A maintainer
 * reading this — the first thing at the decision point — would have concluded
 * the ok lane was exempt and could have restored the very defect that commit
 * removed. The parameter doc 15 lines below was correct the whole time, which
 * did not help: nobody reads the second description first.
 * It does not know the word "github", does not guess at failure wording, and
 * cannot fire on a CLI that writes one reason on one line — which is every
 * other probe in the set, and is also `gh`'s own
 * "You are not logged into any GitHub hosts." case, the only gh failure shape
 * the tests covered before this. A lexical rule would have had to guess at
 * failure text nobody here has captured; this describes a format instead.
 */
function firstReasonLine(
  v: string | Buffer | undefined,
  /**
   * WHICH VERDICT THIS LINE WILL BE ATTACHED TO, so the line can be made to
   * agree with it.
   *
   * ONE RULE, BOTH DIRECTIONS (obi-wan `c5bf33469dc9` on #6204). This started as
   * a boolean `preferFailureMarker` that fired on the failure lane only, and
   * that asymmetry was itself a defect: the heading branch returns the first
   * line under the heading unconditionally, so `host\n  X Failed to log in\n  ✓
   * Logged in` from a CLI that exits 0 selected the `X ` line and persisted
   * `status: 'ok'` beside a message that reads as a failure - the mirror of the
   * defect the parameter was added to remove, and newly reachable because the
   * pre-fix ok path could only ever take the heading itself.
   *
   * Not reachable through `gh auth status`, which exits non-zero on any failure.
   * Reachable through every other binary on this lane, including
   * operator-supplied `connectivity_test.args` overrides - which is the reason
   * the earlier narrowing to "failure path only" was wrong rather than merely
   * incomplete.
   *
   * So the rule is stated once and applied symmetrically: when the line we would
   * report carries the marker that CONTRADICTS the verdict, prefer a later line
   * whose marker AGREES with it. Narrow in both directions - an unmarked leading
   * reason is never demoted, which is what `1e2c82ae0b56` was about.
   */
  lane: 'ok' | 'failure' = 'ok',
): string | undefined {
  // The marker the verdict contradicts, and the marker it agrees with.
  const contradicts = lane === 'failure' ? GH_SUCCESS_MARKER : GH_FAILURE_MARKER;
  const agrees = lane === 'failure' ? GH_FAILURE_MARKER : GH_SUCCESS_MARKER;
  const lines = String(v ?? '').split(/\r?\n/);
  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i] ?? '';
    const trimmed = raw.trim();
    if (trimmed.length === 0) continue;
    // A heading only counts as one if something is indented UNDER it. A lone
    // hostname with nothing below it is the whole message, and taking nothing
    // would be worse than taking it.
    const next = (lines[i + 1] ?? '').replace(/\r$/, '');
    // A HEADING IS UNINDENTED, AND WHAT FOLLOWS IT IS MORE INDENTED THAN IT
    // (obi-wan `414f23a84292` on #6204). The test used to run on `trimmed`, so
    // it fired on ANY bare-token line including an indented one, and the
    // `continue` let it chain. Input "Error\n  Unauthorized\n  - check your
    // token" therefore skipped BOTH "Error" (intended) and "Unauthorized" (the
    // actual reason), leaving a bullet as the operator-facing message.
    //
    // The doc comment above always claimed the narrower rule - a LEADING
    // heading-shaped line - and the code did not implement it. This makes the
    // code match the contract rather than widening the contract to match the
    // code, because `firstReasonLine` runs for every CLI on this lane, not
    // only gh.
    const indent = raw.length - raw.replace(/^\s+/, '').length;
    const nextIndent = next.length - next.replace(/^\s+/, '').length;
    const detailFollows = /^\s+\S/.test(next) && nextIndent > indent;
    const looksLikeHeading = indent === 0 && /^[A-Za-z0-9][A-Za-z0-9.-]*$/.test(trimmed);
    if (detailFollows && looksLikeHeading) {
      // Take the first line UNDER the heading rather than continuing the scan,
      // so the skip cannot chain past the reason.
      const under = lines.slice(i + 1).find((l) => l.trim().length > 0)?.trim();
      // Same narrowing as below, and in both directions: override only a line
      // that itself carries the marker the verdict contradicts. THIS BRANCH is
      // where the ok-lane mirror lived - it returned `under` unconditionally, so
      // an `X ` line directly beneath a heading became an `ok` message.
      const marked =
        under !== undefined && contradicts.test(under)
          ? firstLineMarked(lines, i + 1, agrees)
          : undefined;
      return marked ?? under ?? trimmed;
    }
    // PREFER A FAILING DETAIL LINE OVER THE FIRST ONE (obi-wan `..` on #6204).
    //
    // "the very next line is the reason" held while a host block had one
    // account. Since gh 2.40 one block can list several, and gh prints the
    // HEALTHY one first:
    //
    //   github.com
    //     ✓ Logged in to github.com account alice (keyring)
    //     - Active account: true
    //
    //     X Failed to log in to github.com account bob (GH_TOKEN)
    //
    // `gh auth status` still exits non-zero, so we would have reported
    // `down` with the message "✓ Logged in to github.com account alice" - a
    // verdict whose own evidence contradicts it. The same happens whenever a
    // second host is configured and github.com is the healthy one.
    //
    // So: when the line we would otherwise report carries the marker that
    // CONTRADICTS the verdict, prefer a later line whose marker agrees with it.
    // On the `failure` lane that is the case just described - a `✓` candidate
    // yields to a later `X `. On `ok` it is the mirror: an `X ` candidate yields
    // to a later `✓`.
    //
    // NARROWED TO A MARKED CANDIDATE (obi-wan `..` on #6204). Scanning for a
    // marked failure unconditionally discarded a perfectly good leading reason:
    // input `fatal: could not read from remote` + `X unable to negotiate`
    // reported the second line, because an `X ` anywhere outranked the first.
    // That is not the defect this was added for. So the override fires only when
    // the candidate ITSELF carries the contradicting marker - which is `✓` on the
    // failure lane and `X `/`✗` on the ok lane.
    //
    // THESE TWO PARAGRAPHS SAID "marked as a SUCCESS" AND "only when the
    // candidate IS a `✓` line" UNTIL obi-wan `2133c5c1dcb2`, which is the THIRD
    // stale failure-lane-only claim found in this one function after the symmetry
    // commit - header, test comment, and here. Worth recording rather than
    // quietly fixing: each was true when written and became false when the code
    // moved, and I fixed them one review round at a time instead of grepping the
    // function for every lane claim the first time.
    //
    // Still format-based, not lexical: both markers are glyphs gh prints, and we
    // never guess at failure wording.
    const marked = contradicts.test(trimmed) ? firstLineMarked(lines, i, agrees) : undefined;
    return marked ?? trimmed;
  }
  return undefined;
}

/**
 * gh's own SUCCESS marker at the start of a detail line: `✓` or `✔`.
 *
 * The failure-marker preference fires only when the line we would otherwise
 * report matches this, so an ordinary leading reason is never demoted by an
 * `X ` further down (obi-wan on #6204).
 */
const GH_SUCCESS_MARKER = /^(?:\u2713|\u2714)\s+\S/;

/**
 * gh's own failure marker at the start of a detail line: `✗`, `✘` or `X`.
 *
 * NOT lowercase `x` (obi-wan `1e2c82ae0b56` on #6204). gh emits `X` or `✗`,
 * never `x`, and my earlier comment asserting otherwise was invented rather
 * than observed. Including it only widened the match onto ordinary prose
 * beginning "x " — which on this lane means any CLI's output, not just gh's.
 */
const GH_FAILURE_MARKER = /^(?:\u2717|\u2718|X)\s+\S/;

/**
 * The first line at or after `from` carrying `marker`, or undefined when none
 * does. Takes the marker rather than hard-coding the failure one, so the
 * agreement rule reads identically on both lanes instead of existing as one
 * implemented direction and one comment (obi-wan `c5bf33469dc9` on #6204).
 *
 * Deliberately narrow: it matches gh's marker GLYPHS, not words like "failed" or
 * "expired", because the whole point of `firstReasonLine` is that it describes a
 * FORMAT rather than guessing at failure text nobody here has captured.
 */
function firstLineMarked(lines: string[], from: number, marker: RegExp): string | undefined {
  for (let i = from; i < lines.length; i++) {
    const trimmed = (lines[i] ?? '').trim();
    if (marker.test(trimmed)) return trimmed;
  }
  return undefined;
}

export interface CliProbeOptions {
  timeoutMs?: number;
  /** Extra env for the child (e.g. the integration's API key var). */
  env?: NodeJS.ProcessEnv;
}

/**
 * Log the PATH a did-not-run probe actually searched.
 *
 * THE REASON THIS EXISTS, and why it is a log line rather than a fix.
 *
 * Three tickets have now landed on "the probe cannot see the real binary"
 * (ENG-8902, ENG-9005, ENG-9122) and none of them could establish WHY from the
 * outside. ENG-8902 wrote it down plainly: *"Deliberately NOT fixed here: the
 * reason those hosts' probes cannot see the real `gh`. I could not reproduce
 * that from the environment I have."* ENG-9005's own comment says the same —
 * *"I could not determine from inside a container which of the two the manager
 * actually has"* — and so it ensured a system PATH floor for BOTH possible
 * shapes rather than diagnosing either.
 *
 * On 2026-08-31 the wall was hit again, from the other side. On agt-aws-1:
 *
 *   - `/usr/bin/gh` is present, mode 755, a real 40 MB binary, `gh 2.96.0`
 *   - the SCHEDULED probe reports koda / github `ok`
 *   - the FORCED probe (`debug_probe_integration`, same host, minutes later)
 *     reports `unverified`: "the real gh binary is not on PATH (only the
 *     Augmented shim is)"
 *   - BOTH lanes call `buildProbeEnv`, and the agt-cli deployed there (0.28.748)
 *     is well past the release that added the floor (0.28.715)
 *
 * Every reading contradicts the next, and the one datum that would settle it —
 * the PATH the failing child was actually given — exists for a few microseconds
 * inside a process on a host, and is then gone. The available read-only tooling
 * cannot recover it: the allowlisted SSM diagnostics are a closed enum with no
 * environment dump, and by the time an operator looks, the probe is over.
 *
 * So this does not guess at a fix. It makes the next occurrence answer the
 * question by itself, in the manager log, which `debug_tail_logs` already
 * reaches. A guess would have been cheaper and might even have been right; it
 * would also have been the fourth consecutive change to this area made without
 * evidence.
 *
 * SAFE TO LOG. A PATH is a list of directory names — it is not credential-shaped
 * and is not covered by CREDENTIAL_SHAPED above, which guards values that reach
 * the persisted `status_message`. This goes to the manager log only and is
 * deliberately NOT added to the outcome message: that message IS persisted and
 * is bounded to 200 characters, and a PATH would consume all of it and evict
 * the reason.
 *
 * Only on the two DID-NOT-RUN branches. A probe that ran and failed has a real
 * diagnostic already, and logging a PATH on every ordinary failure would be
 * noise on an hourly loop across every integration on the host.
 */
function logSearchedPath(binary: string, env: NodeJS.ProcessEnv, why: string): void {
  console.warn(
    `[connectivity-probe] ${binary} did not run (${why}). ` +
      `PATH searched: ${effectiveSearchPath(env)}`,
  );
}

/**
 * The command-search path `execFile` will ACTUALLY use for this env — not
 * simply `env.PATH`.
 *
 * The distinction matters more here than it would anywhere else in this file,
 * because this line exists to be believed. It is the evidence a future
 * investigator will use to decide whether the PATH was truncated, and a
 * diagnostic that reports `<unset>` while Node quietly searched a default is
 * worse than no diagnostic — it would send them looking for a missing PATH that
 * was never missing. That is the same failure this whole PR is about, committed
 * by the tool built to prevent it. (CodeRabbit caught it.)
 *
 * Two ways the naive read is wrong:
 *
 *  - **PATH absent.** `execFile` does not give up; the platform supplies a
 *    default (`confstr(_CS_PATH)` via libuv on POSIX, conventionally
 *    `/usr/bin:/bin`). So an env with no PATH still searches somewhere, and
 *    naming that somewhere is the whole point.
 *  - **Windows.** Environment keys are case-insensitive there, so the variable
 *    routinely arrives as `Path`. `env.PATH` is a plain object lookup and would
 *    miss it, reporting `<unset>` for a perfectly ordinary Windows environment.
 *
 * Both are labelled rather than silently substituted: an inferred value must not
 * be indistinguishable from an observed one, which is the habit this file's
 * header spends a hundred lines arguing for.
 */
function effectiveSearchPath(env: NodeJS.ProcessEnv): string {
  if (process.platform === 'win32') {
    const key = Object.keys(env).find((k) => k.toLowerCase() === 'path');
    const value = key ? env[key] : undefined;
    return value ?? '<no PATH/Path key — Windows default lookup applies>';
  }
  return env.PATH ?? '<PATH unset — the platform default applies, conventionally /usr/bin:/bin>';
}

export function runCliProbe(
  binary: string,
  args: string[],
  opts: CliProbeOptions = {},
): Promise<ConnectivityProbeOutcome> {
  const timeoutMs = opts.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const childEnv = opts.env ?? process.env;
  return new Promise((resolve) => {
    execFile(
      binary,
      args,
      { timeout: timeoutMs, env: childEnv, windowsHide: true },
      (err, stdout, stderr) => {
        if (!err) {
          // ENG-10212: the SUCCESS path had the same defect as the failure one,
          // and it was measured on this fleet before the fix - a healthy
          // GitHub row's `status_message` read `gh: github.com`, the section
          // heading, while the line naming the account sat below it. So a
          // working integration and a dead one differed only in the words
          // AROUND the same useless token.
          //
          // That line is marked with gh's SUCCESS glyph, `GH_SUCCESS_MARKER`
          // (`✓`/`✔`), not with `X ` - an earlier version of this comment wrote
          // it as `X Logged in to github.com account <name>`, which is
          // `GH_FAILURE_MARKER` in this same file (obi-wan `721489cc0b0b` on
          // #6204). Read at the ok-path decision point, that would have invited
          // a maintainer to "correct" the two constants and put the
          // down-verdict-quotes-success defect straight back. Naming the
          // constants rather than retyping a glyph is what stops the comment
          // drifting from them again.
          //
          // Fixing it here is worth more than tidiness: the line this now picks
          // up is the one that names the connected account, and WHICH account
          // holds a shared GitHub grant is exactly the question nobody could
          // answer when three agents went down behind one identity.
          //
          // `redactAndBound` still runs over it, and has to: `gh auth status`
          // prints `Token: ghs_...` further down its success block.
          const firstLine = firstReasonLine(stdout, 'ok');
          resolve({
            status: 'ok',
            message: firstLine ? `${binary}: ${redactAndBound(firstLine)}` : `${binary}: ok`,
          });
          return;
        }
        const e = err as NodeJS.ErrnoException & { killed?: boolean; signal?: string };
        if (e.code === 'ENOENT') {
          // ENG-8902: the probe could not run. Say that, rather than asserting
          // the integration is down. See the header for the outage this caused.
          logSearchedPath(binary, childEnv, 'ENOENT — no such binary anywhere on it');
          resolve({
            status: 'unverified',
            // ENG-9173 vocabulary: say WHY in the field that survives. The
            // status alone cannot distinguish this from any other unverified
            // row, and the message below is dropped before persistence.
            cause: 'not_run',
            message:
              `${binary} not found on PATH — the probe could not run, so this is NOT a ` +
              `verdict about the integration. If the agent uses a per-agent binary ` +
              `(e.g. the .claude/agt-bin shim), the probe's PATH must include it.`,
          });
          return;
        }
        if (e.killed || e.signal === 'SIGTERM') {
          // ENG-10529: `unreachable` — "network failure, DNS, or OUR OWN
          // timeout". Precedented rather than invented: `networkOutcome` in
          // connectivity-http-probes.ts makes the identical call for the HTTP
          // lane's AbortError, and its comment is the lesson this ticket is a
          // second instance of — "I added the enum value and then did not set
          // it on the ONE path it describes … Half-classified is its own bug."
          // Not vendor-side, so this still escalates; it just says why now.
          resolve({
            status: 'transient_error',
            cause: 'unreachable',
            message: `${binary} probe timed out after ${timeoutMs / 1000}s`,
          });
          return;
        }
        // ENG-5463 follow-up: classify the failure instead of assuming it.
        //
        // Precision about the original bug, because my first draft of this
        // comment overstated it and a mutation check caught me: stderr was NOT
        // being thrown away. execFile folds captured output into `e.message`,
        // which the old code already interpolated, so the cause did reach the
        // operator. The defect was narrower and entirely about grading —
        // every non-zero exit resolved to `down` no matter what the cause
        // said. Reading `stderr` explicitly below is a message-QUALITY
        // improvement (one clean reason line instead of "Command failed: …"),
        // not what fixes the verdict. Verified: reverting the stderr read
        // alone leaves all five transient cases passing.
        //
        // stdout is included because `gh` prints some failures there, and
        // `e.message` because execFile folds the first chunk of output into it.
        const diagnostic = `${stderr ?? ''}\n${stdout ?? ''}\n${e.message}`;
        // Checked BEFORE the transient signatures, and before the `down`
        // fallback: "the probe never ran" outranks any grading of a result
        // that does not exist. See DID_NOT_RUN_SIGNATURES.
        // `e.code` on a non-zero EXIT is the numeric status (the string codes
        // like 'ENOENT' are spawn failures and were handled above), so anything
        // non-numeric here means "status unknown" and the gate fails closed.
        const exitCode = typeof e.code === 'number' ? e.code : null;
        const didNotRun = didNotRunReasonFor(diagnostic, exitCode);
        if (didNotRun) {
          logSearchedPath(binary, childEnv, didNotRun);
          resolve({
            status: 'unverified',
            // Same reasoning as the ENOENT branch above: this is the arm that
            // catches a shim which started and could not find the tool behind
            // it, which is the shape that actually reaches production.
            cause: 'not_run',
            message:
              `${binary} probe did not run — ${didNotRun}. This is NOT a verdict about the ` +
              `integration: ${firstMeaningfulLine(stderr, stdout, e.message)}`,
          });
          return;
        }
        const transient = transientSignatureFor(diagnostic);
        if (transient) {
          resolve({
            status: 'transient_error',
            // ENG-10529: present only when the matched signature carries one.
            // The broker entry deliberately carries none — see its comment.
            ...(transient.cause ? { cause: transient.cause } : {}),
            message: `${binary} probe inconclusive — ${transient.reason}: ${firstMeaningfulLine(stderr, stdout, e.message)}`,
          });
          return;
        }
        // ENG-10529: the branch that produced every host-probed `unknown` on
        // the fleet. The verdict is unchanged and still counts — this ticket is
        // about making a real failure say WHY, never about softening it.
        resolve({
          status: 'down',
          ...((c) => (c ? { cause: c } : {}))(downCauseFor(diagnostic)),
          message: `${binary} exited non-zero: ${firstMeaningfulLine(stderr, stdout, e.message)}`,
        });
      },
    );
  });
}
